import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { ProjectAddCountryModal } from '../../../projects/widgets/project-add-country-modal/ProjectAddCountryModal'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Square } from '../../../../shared/components/utility/square/Square'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import {
  removeProjectLocation,
  selectProjectLocations,
  selectProjectLocationsChanged,
  setProjectLocations,
  updateProjectLocation,
} from '../../../projects/model/projectsSlice'
import { arraysEqual } from '../../../../shared/lib/helpers/ArrayEqual'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { IconTrash } from '../../../../shared/components/icons/IconTrash'
import { useTranslation } from 'react-i18next'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { ILocation } from '../../../projects/model/types/Location.interface'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { ALL_CITIES_VALUE } from '../../../../shared/consts/AllCities.const'
import styles from './ProjectLocations.module.scss'
import {
  GetFilterCityApiArg,
  useGetFilterBrowserLanguageQuery,
  useGetFilterCityQuery,
  useGetFilterEngineQuery,
} from '../../../../app/api'

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: NodeJS.Timeout

  return (...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

const Location = React.memo(
  ({
    location,
    engines,
    canRemove,
  }: {
    location: ILocation
    engines?: IOption[]
    canRemove: boolean
  }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const filteredEngines = useMemo(
      () =>
        engines?.filter(
          (engine) =>
            !engine.allowed_country_ids?.length ||
            engine.allowed_country_ids.includes(location.country_id),
        ),
      [engines, location.country_id],
    )

    const {
      data: cities,
      isFetching: citiesFetching,
      isLoading: citiesLoading,
    } = useGetFilterCityQuery(
      {
        all: true,
        countryId: location.country_id,
      } as unknown as GetFilterCityApiArg,
      { skip: !location.country_id, refetchOnMountOrArgChange: false },
    )

    const {
      data: browserLanguages,
      isFetching: browserLanguagesFetching,
      isLoading: browserLanguagesLoading,
    } = useGetFilterBrowserLanguageQuery(
      {
        countryId: location.country_id,
      },
      { skip: !location.country_id, refetchOnMountOrArgChange: false },
    )

    const handleRemove = debounce(() => {
      dispatch(removeProjectLocation(location.country_id))
    }, 250)

    if (citiesLoading || browserLanguagesLoading || citiesFetching || browserLanguagesFetching) {
      return (
        <Square className={styles.preloaderSquare}>
          <Preloader />
        </Square>
      )
    }

    return (
      <Square className={styles.square}>
        <div className={styles.header}>
          <h2>{location.country_name}</h2>
          <IconButton disabled={!canRemove} size={'lg'} onClick={handleRemove}>
            <IconTrash color='#00326D' />
          </IconButton>
        </div>
        <div className={styles.fieldsRow}>
          <div className={styles.field}>
            <BpSelect
              multiple
              required
              showChips
              error={location.languages?.length ? '' : t('fields.validate.required')}
              label={t('fields.browserLanguages')}
              loading={browserLanguagesLoading || browserLanguagesFetching}
              options={browserLanguages}
              placeholder={t('fields.selectBrowserLanguages')}
              value={location.languages?.map((language) => String(language.id))}
              onChange={(e) => {
                const value = [].slice.call(e.target.selectedOptions).map((o: IOption) => o.value)

                if (
                  !arraysEqual(
                    value,
                    location.languages?.map((language) => String(language.id)),
                  )
                ) {
                  dispatch(
                    updateProjectLocation({
                      ...location,
                      languages: value.map((id) => ({ id })),
                    }),
                  )
                }
              }}
            />
          </div>
          <div className={styles.field}>
            <BpSelect
              multiple
              required
              showChips
              error={location.engines?.length ? '' : t('fields.validate.required')}
              label={t('fields.searchEngines')}
              options={filteredEngines}
              placeholder={t('fields.selectSearchEngines')}
              value={location.engines?.map((engine) => String(engine.id))}
              onChange={(e) => {
                const value = [].slice.call(e.target.selectedOptions).map((o: IOption) => o.value)

                if (
                  !arraysEqual(
                    value,
                    location.engines?.map((engine) => String(engine.id)),
                  )
                ) {
                  dispatch(
                    updateProjectLocation({
                      ...location,
                      engines: value.map((id) => ({ id: Number(id) })),
                    }),
                  )
                }
              }}
            />
          </div>
          <div className={styles.fieldWide}>
            <BpSelect
              multiple
              required
              showChips
              error={location.cities?.length ? '' : t('fields.validate.required')}
              label={t('fields.cities')}
              loading={citiesLoading || citiesFetching}
              options={cities}
              placeholder={t('fields.selectCities')}
              value={location.cities?.map((city) => String(city.id))}
              onChange={(e) => {
                const selected = [].slice
                  .call(e.target.selectedOptions)
                  .map((o: IOption) => o.value)
                const current = location.cities?.map((city) => String(city.id))
                const hasAllCities = selected.includes(ALL_CITIES_VALUE)
                const hadAllCities = !!current?.includes(ALL_CITIES_VALUE)
                // «All cities» вытесняет остальные города, выбор города — саму опцию
                const value: string[] =
                  hasAllCities && (!hadAllCities || selected.length === 1)
                    ? [ALL_CITIES_VALUE]
                    : selected.filter((id: string) => id !== ALL_CITIES_VALUE)

                if (!arraysEqual(value, current)) {
                  dispatch(
                    updateProjectLocation({
                      ...location,
                      all_cities: value.includes(ALL_CITIES_VALUE),
                      cities: value.map((id) => ({ id: Number(id) })),
                    }),
                  )
                }
              }}
            />
          </div>
        </div>
      </Square>
    )
  },
)

export const ProjectLocations = ({ data, submit, loading }: any) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const locationsList = useAppSelector(selectProjectLocations)
  const locationsChanged = useAppSelector(selectProjectLocationsChanged)
  const [loader, setLoader] = useState<'saveAndAddKeywords' | 'saveChanges'>()
  const dispatch = useAppDispatch()

  const { data: engines, isLoading: enginesLoading } = useGetFilterEngineQuery({})

  const cancel = () => {
    dispatch(setProjectLocations(data?.data.items))
  }

  if (enginesLoading || !locationsList) {
    return <Preloader />
  }

  return (
    <div className={styles.root}>
      <div className={styles.topRow}>
        <p className={styles.description}>{t('pages.project.locationsDescription')}</p>
        <div className={styles.addButtonWrap}>
          <BpButton
            className={styles.addButton}
            iconLeft={<IconPlus />}
            theme={'default'}
            onClick={() => dispatch(setModal({ children: <ProjectAddCountryModal id={id} /> }))}
          >
            {t('buttons.addLocation')}
          </BpButton>
        </div>
      </div>
      <div className={styles.locationsGrid}>
        {locationsList?.map((location: ILocation, index: number) => (
          <Location
            key={index}
            canRemove={locationsList?.length > 1}
            engines={engines}
            location={location}
          />
        ))}
      </div>
      <div className={styles.footer}>
        <BpButton
          disabled={
            !locationsChanged ||
            !locationsList?.length ||
            (loading && loader !== 'saveAndAddKeywords')
          }
          className={styles.footerButton}
          loading={loading && loader === 'saveAndAddKeywords'}
          onClick={() => {
            setLoader('saveAndAddKeywords')
            submit()
          }}
        >
          {t('buttons.saveAndAddKeywords')}
        </BpButton>
        <BpButton
          disabled={
            !locationsChanged || !locationsList?.length || (loading && loader !== 'saveChanges')
          }
          className={styles.footerButton}
          loading={loading && loader === 'saveChanges'}
          theme={'outline'}
          onClick={() => {
            setLoader('saveChanges')
            submit(true)
          }}
        >
          {t('buttons.saveChanges')}
        </BpButton>
        <BpButton
          className={styles.footerButtonTransparent}
          disabled={!locationsChanged}
          theme='transparent'
          onClick={cancel}
        >
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </div>
  )
}
